import { useEffect, useState } from 'react';
import logoPoster from '@/assets/gomin-logo.png';

interface LogoVideoProps {
  className?: string;
  ariaLabel?: string;
}

/**
 * Lazy logo video: renders a lightweight <img> poster on first paint,
 * then mounts the animated <video> after 3s OR first user interaction.
 * Avoids loading the 2.2MB /ai-drvet-logo.mp4 during initial render
 * and prevents the same asset being fetched multiple times in parallel.
 */
export default function LogoVideo({ className = 'h-12 md:h-14 w-auto rounded', ariaLabel = 'Dr.Vet AI animated logo' }: LogoVideoProps) {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    let done = false;
    const trigger = () => {
      if (done) return;
      done = true;
      setShowVideo(true);
      window.removeEventListener('pointerdown', trigger);
      window.removeEventListener('keydown', trigger);
      window.removeEventListener('scroll', trigger);
    };
    const t = window.setTimeout(trigger, 3000);
    window.addEventListener('pointerdown', trigger, { passive: true });
    window.addEventListener('keydown', trigger);
    window.addEventListener('scroll', trigger, { passive: true });
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('pointerdown', trigger);
      window.removeEventListener('keydown', trigger);
      window.removeEventListener('scroll', trigger);
    };
  }, []);

  if (!showVideo) {
    return (
      <img
        src={logoPoster}
        alt={ariaLabel}
        className={className}
        decoding="async"
        loading="eager"
        width={120}
        height={48}
      />
    );
  }

  return (
    <video
      autoPlay
      loop
      muted
      playsInline
      preload="none"
      poster={logoPoster}
      className={className}
      aria-label={ariaLabel}
    >
      <source src="/ai-drvet-logo.webm" type="video/webm" />
      <track kind="captions" src="/captions/empty.vtt" srcLang="en" label="No captions (silent decorative video)" default />
    </video>
  );
}